import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { PREVIEW_DEBOUNCE_MS, useDebouncedValue } from "@/hooks/useDebouncedValue";
import { queryKeys } from "@/queryKeys";
import { analysisRunsService } from "@/services/analysisRuns";
import { borrowerService } from "@/services/borrowers";
import type {
  AnalysisOverridePayload,
  AnalysisOverrideTarget,
  AnalysisRun,
  AnalysisRunListItem,
  CorrectionItem,
  CorrectionPreview,
  CreateAnalysisRunPayload,
} from "@/types/analysisRun";
import {
  applyOverrideOptimistically,
  logTopBarMismatch,
  overrideMutationKey,
  patchBorrowerTransactionsForOverride,
  snapshotTopBar,
  type TopBarSnapshot,
} from "@/utils/optimisticAnalysisRecompute";

const POLL_INTERVAL_MS = 3000;

export function useAnalysisRunsQuery(borrowerId: number | null) {
  return useQuery<AnalysisRunListItem[]>({
    queryKey:
      borrowerId != null
        ? queryKeys.analysisRunsList(borrowerId)
        : (["analysisRuns", "list", null] as const),
    queryFn: () => analysisRunsService.list(borrowerId as number),
    enabled: borrowerId != null,
  });
}

/** Detail query that polls while the async pipeline is still processing. */
export function useAnalysisRunQuery(runId: number | null, borrowerId: number | null) {
  const qc = useQueryClient();
  return useQuery<AnalysisRun>({
    queryKey:
      runId != null
        ? queryKeys.analysisRunsDetail(runId)
        : (["analysisRuns", "detail", null] as const),
    queryFn: async () => {
      const run = await analysisRunsService.get(runId as number);
      if (run.status !== "processing" && borrowerId != null) {
        // Terminal state reached: refresh the (light) list row too.
        qc.invalidateQueries({ queryKey: queryKeys.analysisRunsList(borrowerId) });
      }
      return run;
    },
    enabled: runId != null,
    refetchInterval: (query) =>
      query.state.data?.status === "processing" ? POLL_INTERVAL_MS : false,
  });
}

export function useCreateAnalysisRunMutation(borrowerId: number) {
  const qc = useQueryClient();
  return useMutation<AnalysisRun, unknown, CreateAnalysisRunPayload>({
    mutationFn: analysisRunsService.create,
    onSuccess: (run) => {
      qc.setQueryData<AnalysisRunListItem[]>(queryKeys.analysisRunsList(borrowerId), (prev) => [
        run,
        ...(prev ?? []),
      ]);
      qc.setQueryData(queryKeys.analysisRunsDetail(run.id), run);
    },
  });
}

/**
 * Save or clear a correction. Optimistic UI patches the run + borrower
 * transactions caches immediately (including top-bar aggregates). The
 * server recompute is the source of truth: onSuccess always starts from the
 * official AnalysisRun, then re-layers only still-in-flight optimistic
 * payloads so concurrent edits are not wiped. Same-cell rapid re-edits use
 * latest-wins via a per-key generation counter. Offer inputs stay editable
 * while pending — brief client/server drift is logged, not locked.
 */
export function useAnalysisOverrideMutation(runId: number | null, borrowerId: number) {
  const qc = useQueryClient();
  const pendingGen = useRef<Map<string, number>>(new Map());
  const pendingPayload = useRef<
    Map<
      string,
      | { action: "save"; payload: AnalysisOverridePayload }
      | { action: "clear"; payload: AnalysisOverrideTarget }
    >
  >(new Map());
  const inflight = useRef(0);

  return useMutation<
    AnalysisRun,
    unknown,
    | { action: "save"; payload: AnalysisOverridePayload }
    | { action: "clear"; payload: AnalysisOverrideTarget },
    {
      key: string;
      generation: number;
      prevRun: AnalysisRun | undefined;
      prevTxns: unknown;
      optimistic: TopBarSnapshot | null;
    }
  >({
    mutationFn: (variables) =>
      variables.action === "save"
        ? analysisRunsService.saveOverride(runId as number, variables.payload)
        : analysisRunsService.clearOverride(runId as number, variables.payload),
    onMutate: async (variables) => {
      if (runId == null) {
        return {
          key: "",
          generation: 0,
          prevRun: undefined,
          prevTxns: undefined,
          optimistic: null,
        };
      }
      const key = overrideMutationKey(variables);
      const generation = (pendingGen.current.get(key) ?? 0) + 1;
      pendingGen.current.set(key, generation);
      pendingPayload.current.set(key, variables);
      inflight.current += 1;

      await qc.cancelQueries({ queryKey: queryKeys.analysisRunsDetail(runId) });
      await qc.cancelQueries({
        queryKey: ["borrowerTransactions", Number(borrowerId)] as const,
      });

      const prevRun = qc.getQueryData<AnalysisRun>(queryKeys.analysisRunsDetail(runId));
      const prevTxns = qc.getQueryData(["borrowerTransactions", Number(borrowerId), "statements"]);

      if (prevRun) {
        const optimisticRun = applyOverrideOptimistically(prevRun, variables);
        qc.setQueryData(queryKeys.analysisRunsDetail(runId), optimisticRun);
        qc.setQueriesData(
          { queryKey: ["borrowerTransactions", Number(borrowerId)] },
          (old: unknown) => {
            if (!Array.isArray(old)) return old;
            return patchBorrowerTransactionsForOverride(old, variables);
          }
        );

        return {
          key,
          generation,
          prevRun,
          prevTxns,
          optimistic: snapshotTopBar(optimisticRun),
        };
      }

      return {
        key,
        generation,
        prevRun,
        prevTxns,
        optimistic: null,
      };
    },
    onError: (_err, _variables, context) => {
      if (!context || runId == null) return;
      if (pendingGen.current.get(context.key) !== context.generation) return;
      pendingPayload.current.delete(context.key);
      if (context.prevRun) {
        // Re-apply any other still-pending optimistic payloads on top of the
        // pre-this-mutation snapshot so sibling in-flight edits survive.
        let restored = context.prevRun;
        for (const [k, payload] of pendingPayload.current) {
          if (k === context.key) continue;
          restored = applyOverrideOptimistically(restored, payload);
        }
        qc.setQueryData(queryKeys.analysisRunsDetail(runId), restored);
      }
    },
    onSuccess: (run, variables, context) => {
      if (context?.optimistic) {
        const official = snapshotTopBar(run);
        if (official) {
          const overrideMeta =
            variables.action === "save"
              ? {
                  target_type: variables.payload.target_type,
                  target_key: variables.payload.target_key,
                  field: variables.payload.field,
                  value: variables.payload.value,
                }
              : {
                  target_type: variables.payload.target_type,
                  target_key: variables.payload.target_key,
                  field: variables.payload.field,
                };
          logTopBarMismatch({
            runId: run.id,
            override: overrideMeta,
            optimistic: context.optimistic,
            official,
          });
        }
      }

      if (context && pendingGen.current.get(context.key) !== context.generation) {
        return;
      }
      if (context) pendingPayload.current.delete(context.key);

      // Official run includes all overrides committed before this response.
      // Re-apply any still-in-flight optimistic payloads so concurrent edits
      // on other keys are not wiped.
      let next = run;
      for (const payload of pendingPayload.current.values()) {
        next = applyOverrideOptimistically(next, payload);
      }
      qc.setQueryData(queryKeys.analysisRunsDetail(run.id), next);
      qc.invalidateQueries({ queryKey: queryKeys.analysisRunsList(borrowerId) });
    },
    onSettled: () => {
      inflight.current = Math.max(0, inflight.current - 1);
      if (inflight.current === 0) {
        pendingPayload.current.clear();
        qc.invalidateQueries({
          queryKey: ["borrowerTransactions", Number(borrowerId)] as const,
        });
      }
    },
  });
}

/**
 * Re-run statement AI analysis over the borrower's uploaded files.
 *
 * Hits `/rerun-analysis/`; the server starts a new AnalysisRun (force=true, so an
 * existing run over the same statements does not short-circuit it) with
 * bypass_stage1_cache so stages 0/1 always re-call the models. The run
 * list is invalidated so the tab picks up the processing run and polls it.
 */
export function useRerunStatementAnalysisMutation(borrowerId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (borrowerId == null) throw new Error("No borrower ID");
      return borrowerService.rerunAnalysis(borrowerId, undefined, false);
    },
    onSuccess: () => {
      if (borrowerId == null) return;
      qc.invalidateQueries({ queryKey: queryKeys.analysisRunsList(borrowerId) });
      qc.invalidateQueries({ queryKey: queryKeys.borrower(borrowerId) });
      qc.invalidateQueries({ queryKey: ["borrowerTransactions", Number(borrowerId)] as const });
    },
  });
}

/**
 * Re-run the Plaid-lane AI analysis over the latest asset report. The
 * result lands on the borrower's Plaid UnderwritingDecision, which the Final
 * Underwriting tab renders, so the borrower detail is what gets invalidated.
 */
export function useRerunPlaidAnalysisMutation(borrowerId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (borrowerId == null) throw new Error("No borrower ID");
      return borrowerService.plaidRetryAnalysis(borrowerId, true);
    },
    onSuccess: () => {
      if (borrowerId == null) return;
      qc.invalidateQueries({ queryKey: queryKeys.borrower(borrowerId) });
      qc.invalidateQueries({ queryKey: queryKeys.analysisRunsList(borrowerId) });
    },
  });
}

export function useStopAnalysisRunMutation(borrowerId: number) {
  const qc = useQueryClient();
  return useMutation<AnalysisRun, unknown, number>({
    mutationFn: analysisRunsService.stop,
    onSuccess: (run) => {
      qc.setQueryData(queryKeys.analysisRunsDetail(run.id), run);
      qc.setQueryData<AnalysisRunListItem[]>(queryKeys.analysisRunsList(borrowerId), (prev) =>
        prev?.map((r) => (r.id === run.id ? { ...r, status: run.status } : r))
      );
    },
  });
}

/**
 * What a set of corrections would change on the lane's latest analysis (an approval card's
 * Impact for its ticked rows), asked again 300 ms after the items stop changing. The last answer
 * stays shown while the next is on its way; no items, no request. `current` is whether the data
 * is the answer for these `items` (not the last one's, nor one still waiting out the debounce).
 */
export function useCorrectionsPreviewQuery(
  borrowerId: number | null,
  items: CorrectionItem[],
  source: "statements" | "plaid" = "statements"
) {
  const debounced = useDebouncedValue(items, PREVIEW_DEBOUNCE_MS);
  const query = useQuery<CorrectionPreview>({
    queryKey: queryKeys.analysisCorrectionsPreview(borrowerId ?? 0, source, debounced),
    queryFn: () => borrowerService.previewAnalysisCorrections(borrowerId!, debounced, source),
    enabled: borrowerId != null && debounced.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
  });
  // Only what the card reads, so it re-renders on those alone (the query tracks the props read).
  const { data, isError, isPlaceholderData, isFetching } = query;
  const current = debounced === items && data !== undefined && !isPlaceholderData && !isFetching;
  return { data, isError, current };
}
